import { memo } from "react";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import { TabCard } from "../../tabs/tab-card";
import { RedoBarChart } from "../bar-chart/redo-bar-chart";
import { RedoLineAndBarChart } from "../line-and-bar-chart/redo-line-and-bar-chart";
import { ChartSeries } from "../line-and-bar-chart/types";

/** Default palette for categories without an explicit color. */
export const DEFAULT_PALETTE = [
  "#8B5CF6",
  "#06B6D4",
  "#84CC16",
  "#F97316",
  "#EC4899",
  "#6B7280",
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#F87171",
  "#60A5FA",
  "#34D399",
  "#FBBF24",
  "#A78BFA",
];

export interface InsightsBreakdownDatum {
  label: string;
  value: number;
  /** Overrides the palette color for this category's bar. */
  color?: string;
}

export interface InsightsBreakdownGraphProps {
  /** Per-category totals rendered as a bar chart. */
  totals: readonly InsightsBreakdownDatum[];
  /** Pre-shaped time series; when provided the graph gains a Timeline tab. Omit for a totals-only chart. */
  timelineSeries?: readonly ChartSeries[];
  totalsTabLabel?: string;
  timelineTabLabel?: string;
  leftAxisLabel?: string;
  palette?: readonly string[];
  emptyTotalsText?: string;
  emptyTimelineText?: string;
  onTotalsBarClick?: (label: string) => void;
  /** Noun used in timeline tooltips, e.g. "conversations". */
  valueNoun?: string;
  defaultTabKey?: string;
}

const roundFormatter = (value: number) => `${Math.round(value)}`;

const EmptyState = memo(function EmptyState({ text }: { text: string }) {
  return (
    <Flex align="center" justify="center" p="lg">
      <Text>{text}</Text>
    </Flex>
  );
});

/** Generic sentiment/topic/handled-by breakdown: a per-category bar chart, optionally with a time-series tab. Presentational only. */
export const InsightsBreakdownGraph = memo(function InsightsBreakdownGraph({
  totals,
  timelineSeries,
  totalsTabLabel = "Totals",
  timelineTabLabel = "Timeline",
  leftAxisLabel = "Number of conversations",
  palette = DEFAULT_PALETTE,
  emptyTotalsText = "No data available.",
  emptyTimelineText = "No data available for the selected date range.",
  onTotalsBarClick,
  valueNoun = "conversations",
  defaultTabKey = "timeline",
}: InsightsBreakdownGraphProps) {
  const totalsContent =
    totals.length === 0 ? (
      <EmptyState text={emptyTotalsText} />
    ) : (
      <RedoBarChart
        labels={totals.map((datum) => datum.label)}
        legendPosition="top"
        onBarClick={onTotalsBarClick}
        series={[
          {
            name: "Conversations",
            data: totals.map((datum) => datum.value),
            color: totals.map(
              (datum, index) =>
                datum.color ?? palette[index % palette.length] ?? "#6B7280",
            ),
          },
        ]}
        tooltipFormat={{
          showSeriesNames: false,
          valueFormatter: roundFormatter,
        }}
        valueAxisFormatter={roundFormatter}
      />
    );

  if (timelineSeries === undefined) {
    return totalsContent;
  }

  const timelineContent =
    timelineSeries.length === 0 ? (
      <EmptyState text={emptyTimelineText} />
    ) : (
      <RedoLineAndBarChart
        allowGraphTypeSwitch
        chartStyle="line"
        leftAxisLabel={leftAxisLabel}
        legendPosition="top"
        series={[...timelineSeries]}
        showTooltipSeriesNames
        valueAxisFormatter={roundFormatter}
        valueTooltipFormatter={(value) => `${Math.round(value)} ${valueNoun}`}
      />
    );

  return (
    <TabCard
      defaultTabKey={defaultTabKey}
      tabs={[
        { key: "timeline", label: timelineTabLabel, content: timelineContent },
        { key: "totals", label: totalsTabLabel, content: totalsContent },
      ]}
    />
  );
});
